import React, { useState } from 'react';
import { canonicalCategoryIds } from '../data/defaultData';

function boldNo(text) {
  if (!text) return text;
  const parts = String(text).split(/([A-Za-z\u00C0-\u017F][A-Za-z\u00C0-\u017F'\u2019-]*)/);
  let k = 0;
  return parts.map((p) => (/^[A-Za-z\u00C0-\u017F]/.test(p)
    ? <strong key={k++} className="ord-pv-no">{p}</strong>
    : <span key={k++}>{p}</span>));
}

function isComparisonForm(form, base) {
  const f = String(form || '').trim().toLowerCase();
  const b = String(base || '').trim().toLowerCase();
  if (!f || f === b) return false;
  // Надёжные окончания степеней сравнения: -ere (сравнит.), -est/-este (превосх.).
  // Обычные формы мн./опред. оканчиваются на одиночное -e (store, slemme) и сюда не попадают.
  return /(ere|este|est)$/.test(f) && f.length > b.length;
}

const REQUIRED_KEYS = ['word','pos','gender','translations','phrase','phrase_translation','synonyms','antonyms','root','cognates','examples','example_translations','story','phonetic_similar','etymology'];

export function validateOrdJson(obj) {
  const errors = [];
  const warnings = [];
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { errors: ['Файл не похож на нужный JSON-объект.'], warnings: [] };
  }
  REQUIRED_KEYS.forEach((k) => { if (!(k in obj)) errors.push('Не хватает поля: ' + k); });
  const arr = (v) => (Array.isArray(v) ? v : []);
  const tr = arr(obj.translations);
  if (tr.length < 1) errors.push('Нужен хотя бы один перевод слова.');
  if (tr.length > 5) warnings.push('Переводов больше 5 — лишние не используем.');
  if (arr(obj.examples).length !== 3) errors.push('Нужно ровно 3 примера.');
  if (arr(obj.example_translations).length !== 3) errors.push('Нужно ровно 3 перевода примеров.');
  const ph = arr(obj.phonetic_similar);
  if (ph.length !== 5) warnings.push('Фонетически похожих слов не 5, а ' + ph.length + ' (можно оставить так).');
  if ((obj.phrase || '').length > 120) warnings.push('Фраза длиннее 120 символов — обрежем.');
  if ((obj.phrase_translation || '').length > 160) warnings.push('Перевод фразы длиннее 160 — обрежем.');
  arr(obj.examples).forEach((e, i) => { if ((e || '').length > 200) warnings.push('Пример ' + (i + 1) + ' длиннее 200 — обрежем.'); });
  if ((obj.story || '').length > 1000) warnings.push('История длиннее 1000 — обрежем.');
  if ((obj.etymology || '').length > 1000) warnings.push('Этимология длиннее 1000 — обрежем.');
  return { errors, warnings };
}

function makeThumb(dataUrl) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.onload = () => {
        const max = 400;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const cw = Math.round(img.width * scale);
        const ch = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, cw, ch);
        resolve(canvas.toDataURL('image/jpeg', 0.5));
      };
      img.onerror = reject;
      img.src = dataUrl;
    } catch (e) { reject(e); }
  });
}

export default function OrdskjonnhetDraft({ word, onClose, onSave, onAuthor, onPhoto, wordSet, onOpenWord, onViewPublic, categoryColor, canPublish = false }) {
  const [result, setResult] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [jsonText, setJsonText] = useState("");
  const [appliedText, setAppliedText] = useState("");
  const [fileName, setFileName] = useState('');
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editedJson, setEditedJson] = useState(null);
  const [localPhoto, setLocalPhoto] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [assoc, setAssoc] = useState((word && word.ord && word.ord.assoc) || "");

  const hasData = Boolean(
    (word && Array.isArray(word.translations) && word.translations.length) ||
    (word && Array.isArray(word.synonyms) && word.synonyms.length) ||
    (word && word.story) || (word && word.ord)
  );

  const handleSave = (mode) => {
    if (!parsed || !onSave) return;
    onSave(parsed, mode);
    setSaved(true);
  };

  const GENDER_COLORS = { en: '#2563eb', ei: '#be185d', et: '#475569' };
  const POS_TYPE = { substantiv: 'noun', verb: 'verb', adjektiv: 'adjective', adverb: 'adverb' };
  const TYPE_POS = { noun: 'substantiv', verb: 'verb', adjective: 'adjektiv', adverb: 'adverb' };

  const arrOf = (v) => (Array.isArray(v) ? v : []);
  const gradOf = (g) => ({ positiv: (g && g.positiv) || "", komparativ: (g && g.komparativ) || "", superlativ: (g && g.superlativ) || "" });
  const vm = (() => {
    const base = word || {};
    const ord = base.ord || {};
    const wordRoot = (Array.isArray(base.roots) && base.roots[0] && base.roots[0].root) || base.root || '';
    const pickPhoto = (...cands) => cands.find((ph) => ph && ph.dataUrl) || null;
    const wordPhoto = pickPhoto(localPhoto, ord && ord.photo, base.photo, base.levels && base.levels.A1 && base.levels.A1.photo);
    const src = editedJson || parsed;
    if (src) {
      return {
        text: base.text || src.word || '', type: POS_TYPE[src.pos] || base.type || '', pos: src.pos || '',
        gender: src.gender || base.gender || '', phrase: src.phrase || '', phraseTranslation: src.phrase_translation || '',
        translations: arrOf(src.translations), synonyms: arrOf(src.synonyms), antonyms: arrOf(src.antonyms),
        root: src.root || wordRoot, cognates: arrOf(src.cognates),
        examples: arrOf(src.examples), exampleTranslations: arrOf(src.example_translations),
        story: src.story || '', phonetic: arrOf(src.phonetic_similar), etymology: src.etymology || '',
        grad: gradOf(src.gradboyning),
        photo: wordPhoto,
        photoReady: Boolean(localPhoto) || Boolean(ord.photoReady),
        authored: Boolean(ord.authored), fullnessDate: ord.fullnessDate || null,
      };
    }
    if (base.text || base.ord) {
      const ph = (base.levels && base.levels.A1 && base.levels.A1.phrase) || base.phrase || '';
      return {
        text: base.text || '', type: base.type || '', pos: TYPE_POS[base.type] || '',
        gender: base.gender || '', phrase: ph, phraseTranslation: ord.phrase_translation || '',
        translations: arrOf(base.translations), synonyms: arrOf(base.synonyms), antonyms: arrOf(base.antonyms),
        root: wordRoot, cognates: arrOf(ord.cognates),
        examples: arrOf(ord.examples), exampleTranslations: arrOf(ord.example_translations),
        story: base.story || '', phonetic: arrOf(ord.phonetic_similar), etymology: ord.etymology || '',
        grad: gradOf(ord.gradboyning),
        photo: wordPhoto,
        photoReady: Boolean(localPhoto) || Boolean(ord.photoReady),
        authored: Boolean(ord.authored), fullnessDate: ord.fullnessDate || null,
      };
    }
    return null;
  })();

  const posLabel = (() => {
    if (!vm) return '';
    if (vm.pos) return vm.pos.charAt(0).toUpperCase() + vm.pos.slice(1);
    const m = { noun: 'Substantiv', verb: 'Verb', adjective: 'Adjektiv', adverb: 'Adverb' };
    return m[vm.type] || '';
  })();

  const checks = (() => {
    if (!vm) return { items: [], score: 0, total: 0, complete: false };
    const cnt = (a) => (Array.isArray(a) ? a.filter(Boolean).length : 0);
    const items = [
      { label: 'Слово', ok: !!vm.text },
      { label: 'Часть речи', ok: !!(vm.type || vm.pos) },
    ];
    if (vm.type === 'noun') items.push({ label: 'Род (en/ei/et)', ok: ['en', 'ei', 'et'].includes(vm.gender) });
    const birds = assoc === 'птицы';
    const tool = assoc === 'инструмент';
    const antOptional = birds || tool;
    const cogOptional = birds;
    items.push(
      { label: 'Перевод (хотя бы 1)', ok: vm.translations.length >= 1 },
      { label: 'Фраза-пример', ok: !!vm.phrase },
      { label: '3 примера', ok: cnt(vm.examples) >= 3 },
      { label: 'История', ok: !!vm.story },
      { label: 'Фото (≥800px)', ok: !!vm.photoReady, optional: vm.type === 'phrase' },
      { label: 'Синонимы (≥1)', ok: vm.synonyms.length >= 1 },
      { label: 'Антонимы (≥1)', ok: vm.antonyms.length >= 1, optional: antOptional },
      { label: 'Корень', ok: !!vm.root },
      { label: 'Однокоренные (≥1)', ok: vm.cognates.length >= 1, optional: cogOptional },
      { label: 'Похожие по звучанию (≥3)', ok: cnt(vm.phonetic) >= 3 },
      { label: 'Степени сравнения', ok: !!(vm.grad && vm.grad.positiv && vm.grad.komparativ && vm.grad.superlativ), optional: vm.type !== 'adjective' },
    );
    const required = items.filter((it) => !it.optional);
    const score = required.filter((it) => it.ok).length;
    return { items, score, total: required.length, complete: score === required.length };
  })();

  const pct = checks.total ? Math.round((checks.score / checks.total) * 100) : 0;

  const collectForms = () => {
    const f = { ...((word && word.forms) || {}), ...((word && word.manualForms) || {}) };
    const base = String((vm && vm.text) || (word && word.text) || '').trim();
    if (vm && vm.type === 'noun') {
      const g = vm.gender;
      const first = (g === 'en' || g === 'ei' || g === 'et') ? (g + ' ' + base) : base;
      return [first, f.singularDefinite, f.pluralIndefinite, f.pluralDefinite]
        .map((v) => String(v || '').trim()).filter(Boolean);
    }
    const vals = [base, ...Object.values(f)].map((v) => String(v || '').trim()).filter(Boolean);
    let uniq = Array.from(new Set(vals));
    if (vm && vm.type === 'adjective') {
      const baseW = String((vm && vm.text) || '').trim().toLowerCase();
      const gset = new Set([vm.grad && vm.grad.positiv, vm.grad && vm.grad.komparativ, vm.grad && vm.grad.superlativ].map((x) => String(x || '').trim()).filter(Boolean));
      uniq = uniq.filter((v) => !gset.has(v) && !isComparisonForm(v, baseW));
    }
    return uniq;
  };
  const makePublicData = () => (vm ? {
    text: vm.text, type: vm.type, pos: vm.pos, gender: vm.gender,
    phrase: vm.phrase, phraseTranslation: vm.phraseTranslation,
    grad: vm.grad,
    translations: vm.translations, synonyms: vm.synonyms, antonyms: vm.antonyms,
    root: vm.root, cognates: vm.cognates, forms: collectForms(),
    examples: vm.examples, exampleTranslations: vm.exampleTranslations,
    story: vm.story, phonetic: vm.phonetic.filter((s) => s && String(s).trim()), etymology: vm.etymology,
    photo: vm.photo && vm.photo.dataUrl ? { name: vm.photo.name || '' } : null,
  } : null);

  const signNow = async () => {
    if (!onAuthor) return;
    // Сохраняем данные и в само слово (а не только в облако), чтобы после перезагрузки текст не пропадал.
    const srcForSave = editedJson || parsed;
    if (srcForSave && onSave) onSave(srcForSave, 'replace');
    const data = makePublicData();
    let fullPhoto = null;
    if (vm && vm.photo && vm.photo.dataUrl) {
      fullPhoto = vm.photo.dataUrl;
      try { const t = await makeThumb(fullPhoto); if (data && data.photo) data.photo.thumb = t; } catch (e) {}
    }
    onAuthor(true, { data, completeness: checks.score, fullPhoto, assoc });
  };

  const normRel = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const renderRelWords = (arr) => (arr || []).map((s, i) => {
    const exists = wordSet && wordSet.has(normRel(s)) && normRel(s) !== normRel(vm && vm.text);
    return exists
      ? <button key={i} type="button" className="ord-pv-word-chip ord-link" onClick={() => onOpenWord && onOpenWord(s)}>{s}</button>
      : <span key={i} className="ord-pv-word-chip">{s}</span>;
  });

  const renderPhrase = (phrase, headword, color) => {
    if (!phrase) return phrase;
    const clean = (s) => String(s || '').trim().toLowerCase().replace(/^(en|ei|et|\u00e5)\s+/i, '');
    const stem = clean(headword);
    if (!stem) return phrase;
    const exact = new Set([stem, ...collectForms().map(clean)].filter(Boolean));
    const parts = String(phrase).split(/([A-Za-zÀ-ÿ]+)/);
    let k = 0;
    return parts.map((p) => {
      const low = p.toLowerCase();
      const isWord = /[A-Za-zÀ-ÿ]/.test(p);
      const hit = isWord && (exact.has(low) || low.startsWith(stem));
      return hit
        ? <mark key={k++} style={{ color, background: 'transparent', fontWeight: 700 }}>{p}</mark>
        : <span key={k++}>{p}</span>;
    });
  };

  const speakNorwegian = (text, rate) => {
    const t = (text == null ? '' : String(text)).trim();
    if (!t) return;
    try { const a = new Audio('/tts/synth?text=' + encodeURIComponent(t) + (rate ? '&rate=' + rate : '')); a.play().catch(() => {}); } catch (e) { /* tts off */ }
  };
  const speakWord = () => {
    if (!vm) return;
    const t = String(vm.text || '').replace(/^\s*(en|ei|et|\u00e5)\s+/i, '').trim();
    speakNorwegian(t);
  };

  const toList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
  const fromList = (a) => (Array.isArray(a) ? a.join(', ') : '');

  const startEdit = () => {
    if (!vm) return;
    const pad = (a, n) => { const r = Array.isArray(a) ? a.slice(0, n) : []; while (r.length < n) r.push(''); return r; };
    setEdit({
      word: vm.text, pos: vm.pos || '', gender: vm.gender || '',
      translations: vm.translations.slice(), phrase: vm.phrase || '', phrase_translation: vm.phraseTranslation || '',
      synonyms: vm.synonyms.slice(), antonyms: vm.antonyms.slice(), root: vm.root || '', cognates: vm.cognates.slice(),
      examples: pad(vm.examples, 3), example_translations: pad(vm.exampleTranslations, 3),
      story: vm.story || '', phonetic_similar: pad(vm.phonetic, 5), etymology: vm.etymology || '',
      gradboyning: { positiv: (vm.grad && vm.grad.positiv) || '', komparativ: (vm.grad && vm.grad.komparativ) || '', superlativ: (vm.grad && vm.grad.superlativ) || '' },
    });
    setEditing(true);
  };

  const setE = (key, value) => setEdit((e) => ({ ...e, [key]: value }));
  const setEArr = (key, i, value) => setEdit((e) => { const a = e[key].slice(); a[i] = value; return { ...e, [key]: a }; });
  const setEList = (key, value) => setEdit((e) => ({ ...e, [key]: toList(value) }));
  const setEGrad = (sub, value) => setEdit((e) => ({ ...e, gradboyning: { ...(e.gradboyning || {}), [sub]: value } }));

  const saveEdit = () => {
    if (!edit || !onSave) return;
    onSave(edit, 'replace');
    setEditedJson(edit);
    setEditing(false);
  };

  const handlePagePhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setPhotoError('Файл больше 5 МБ (' + (file.size / 1048576).toFixed(1) + ' МБ). Нужно меньше 5 МБ.'); if (e.target) e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth, h = img.naturalHeight;
        const minSide = Math.min(w, h);
        if (minSide < 800) { setPhotoError('Фото слишком маленькое: меньшая сторона ' + minSide + 'px, нужно не меньше 800px.'); return; }
        setPhotoError('');
        const scale = minSide > 1280 ? 1280 / minSide : 1;
        const cw = Math.round(w * scale), ch = Math.round(h * scale);
        let outUrl = dataUrl;
        try {
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, cw, ch);
          outUrl = canvas.toDataURL('image/jpeg', 0.85);
        } catch (err) { outUrl = dataUrl; }
        const photoObj = { dataUrl: outUrl, name: file.name, type: 'image/jpeg' };
        setLocalPhoto(photoObj);
        if (onPhoto) onPhoto(photoObj);
      };
      img.onerror = () => setPhotoError('Не удалось прочитать изображение.');
      img.src = dataUrl;
    };
    reader.onerror = () => setPhotoError('Не удалось прочитать файл.');
    reader.readAsDataURL(file);
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setSaved(false);
    setEditing(false);
    setEditedJson(null);
    const reader = new FileReader();
    reader.onload = () => {
      let obj;
      try {
        obj = JSON.parse(String(reader.result));
      } catch (err) {
        setParsed(null);
        setResult({ errors: ['Файл повреждён или это не JSON. Проверьте, что вставлен ответ ИИ целиком.'], warnings: [] });
        return;
      }
      const r = validateOrdJson(obj);
      setParsed(r.errors.length ? null : obj);
      setResult(r);
    };
    reader.onerror = () => { setParsed(null); setResult({ errors: ['Не удалось прочитать файл.'], warnings: [] }); };
    reader.readAsText(file, 'utf-8');
  };

  const applyJsonText = () => {
    const str = (jsonText || '').trim();
    if (!str) return;
    setAppliedText(str);
    setSaved(false);
    setEditing(false);
    setEditedJson(null);
    setFileName('');
    let obj;
    try {
      obj = JSON.parse(str);
    } catch (err) {
      setParsed(null);
      setResult({ errors: ['Это не похоже на JSON. Проверьте, что вставлен ответ ИИ целиком.'], warnings: [] });
      return;
    }
    const r = validateOrdJson(obj);
    setParsed(r.errors.length ? null : obj);
    setResult(r);
  };

  return (
    <div className="ord-draft-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ord-draft" onClick={(e) => e.stopPropagation()}>
        <div className="ord-draft-head">
          <div className="ord-draft-brand">
            <img className="ord-draft-logo" src="/norsk-logo.png" alt="" />
            <div className="ord-draft-copy">
              <span className="ord-draft-name">Norsk Pro</span>
              <span className="ord-draft-tag">word memorization system</span>
            </div>
          </div>
          <span className="ord-draft-title">Ordskjønnhet</span>
          <button type="button" className="ord-draft-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>
        <div className="ord-draft-body">
          {vm && !editing && (
            <div className="ord-pv">
              <div className="ord-pv-word">
                {vm.type === 'noun' && ['en', 'ei', 'et'].includes(vm.gender) && (
                  <span className="ord-pv-affix" style={{ color: GENDER_COLORS[vm.gender] }}>{vm.gender} </span>
                )}
                {vm.type === 'verb' && <span className="ord-pv-affix">å </span>}
                <span className="ord-pv-headword">{vm.text}</span>
                <button type="button" className="ord-pv-tts" onClick={speakWord} aria-label="Прослушать слово">🔊</button>
              </div>
              {vm.phrase && (
                <p className="ord-pv-phrase">{renderPhrase(vm.phrase, vm.text, GENDER_COLORS[vm.gender] || categoryColor || '#6d28d9')} <button type="button" className="ord-pv-tts" onClick={() => speakNorwegian(vm.phrase, 0.75)} aria-label="Прослушать фразу">🔊</button></p>
              )}
              {vm.phraseTranslation && <p className="ord-pv-phrase-tr">{vm.phraseTranslation}</p>}
              {vm.translations.length > 0 && (
                <div className="ord-pv-tr">{vm.translations.map((t, i) => <span key={i} className="ord-pv-chip">{t}</span>)}</div>
              )}
              <div className="ord-pv-photo-wrap">
                {vm.photo && vm.photo.dataUrl ? (
                  <div className="ord-pv-photo-frame">
                    <img className="ord-pv-photo" src={vm.photo.dataUrl} alt={vm.text} />
                    <div className="ord-pv-photo-brand">
                      <img className="ord-pv-photo-logo" src="/norsk-logo.png" alt="" />
                      <span>Norsk Pro</span>
                    </div>
                  </div>
                ) : (
                  <div className="ord-pv-photo-empty">Фото страницы не загружено</div>
                )}
                {!vm.authored && (
                  <label className="ord-pv-photo-btn">
                    <input type="file" accept="image/*" onChange={handlePagePhoto} />
                    {vm.photo && vm.photo.dataUrl ? 'Заменить фото' : 'Загрузить фото (≥800px)'}
                  </label>
                )}
                {photoError && <p className="ord-pv-photo-err">{photoError}</p>}
              </div>
              {collectForms().length > 0 && (
                <div className="ord-pv-tr ord-pv-forms">{collectForms().map((f, i) => <span key={i} className="ord-pv-chip">{f}</span>)}</div>
              )}
              {vm.examples.length > 0 && (
                <div className="ord-pv-examples">
                  {vm.examples.map((ex, i) => (
                    <div key={i} className="ord-pv-example">
                      <p className="ord-pv-ex">{renderPhrase(ex, vm.text, GENDER_COLORS[vm.gender] || categoryColor || '#6d28d9')}</p>
                      {vm.exampleTranslations[i] && <p className="ord-pv-ex-tr">{vm.exampleTranslations[i]}</p>}
                    </div>
                  ))}
                </div>
              )}
              {vm.story && (
                <div className="ord-pv-block"><h4>История</h4><p className="ord-pv-text">{boldNo(vm.story)}</p></div>
              )}
              {vm.synonyms.length > 0 && (
                <div className="ord-pv-block"><h4>Синонимы</h4><div className="ord-pv-words">{renderRelWords(vm.synonyms)}</div></div>
              )}
              {vm.antonyms.length > 0 && (
                <div className="ord-pv-block"><h4>Антонимы</h4><div className="ord-pv-words">{renderRelWords(vm.antonyms)}</div></div>
              )}
              {vm.root && (
                <div className="ord-pv-block"><div className="ord-pv-root-head"><h4>Корень</h4><div className="ord-pv-words">{renderRelWords([vm.root])}</div></div>{vm.cognates.length > 0 && (<div className="ord-pv-words ord-pv-cognates">{renderRelWords(vm.cognates)}</div>)}</div>
              )}
              {vm.phonetic.filter((s) => s && String(s).trim()).length > 0 && (
                <div className="ord-pv-block"><h4>Похожие по звучанию</h4><div className="ord-pv-words">{vm.phonetic.filter((s) => s && String(s).trim()).map((s, i) => <span key={i} className="ord-pv-word-chip">{String(s).trim()}</span>)}</div></div>
              )}
              {vm.type === 'adjective' && vm.grad && (vm.grad.positiv || vm.grad.komparativ || vm.grad.superlativ) && (
                <div className="ord-pv-block"><h4>Степени сравнения</h4><div className="ord-pv-words">
                  {vm.grad.positiv && <span className="ord-pv-word-chip">{vm.grad.positiv}</span>}
                  {vm.grad.komparativ && <span className="ord-pv-word-chip">{vm.grad.komparativ}</span>}
                  {vm.grad.superlativ && <span className="ord-pv-word-chip">{vm.grad.superlativ}</span>}
                </div></div>
              )}
              <div className="ord-pv-block ord-pv-assoc"><h4>Ассоциации</h4>
                <div className="ord-pv-words">
                  {['птицы', 'инструмент'].map((a) => (
                    <button key={a} type="button" className={'ord-pv-word-chip ord-assoc-chip' + (assoc === a ? ' active' : '')} onClick={() => setAssoc(assoc === a ? '' : a)}>{a}</button>
                  ))}
                </div>
                {assoc === 'птицы' && <p className="ord-pv-muted">Для «птиц» антонимы и однокоренные необязательны.</p>}
                {assoc === 'инструмент' && <p className="ord-pv-muted">Для «инструментов» антонимы необязательны.</p>}
              </div>
              {posLabel && (
                <div className="ord-pv-block"><div className="ord-pv-root-head"><h4>Часть речи</h4><div className="ord-pv-words"><span className="ord-pv-word-chip">{posLabel}</span></div></div></div>
              )}
              {vm.etymology && (
                <div className="ord-pv-block"><h4>Этимология</h4><p className="ord-pv-text">{boldNo(vm.etymology)}</p></div>
              )}
              {vm.authored && vm.fullnessDate && (
                <p className="ord-pv-date">Обновлено: {new Date(vm.fullnessDate).toLocaleDateString('ru-RU')}</p>
              )}
            </div>
          )}
          {vm && !editing && (
            <div className="ord-check">
              <div className="ord-check-bar"><div className="ord-check-fill" style={{ width: pct + '%' }} /></div>
              <p className="ord-check-pct">Полнота: {pct}% ({checks.score} из {checks.total})</p>
              <ul className="ord-check-list">
                {checks.items.map((it, i) => (
                  <li key={i} className={(it.ok ? 'ok' : 'no') + (it.optional ? ' optional' : '')}>{it.ok ? '✓' : '○'} {it.label}{it.optional ? ' (необязательно)' : ''}</li>
                ))}
              </ul>
              {vm.authored ? (
                <div className="ord-author-done">
                  <strong>Подписано автором</strong>
                  {vm.fullnessDate && <span> · {new Date(vm.fullnessDate).toLocaleDateString('ru-RU')}</span>}
                  <button type="button" className="ord-draft-save ghost" onClick={() => onAuthor && onAuthor(false)}>Снять подпись</button>
                </div>
              ) : canPublish ? (
                <button type="button" className="ord-draft-save" disabled={!checks.complete} onClick={signNow}>
                  {checks.complete ? 'Подписать как автора' : 'Подписать (нужно 100%)'}
                </button>
              ) : (
                <p className="ord-author-locked">Опубликовать слово для всех могут пользователи с платной подпиской и администраторы. Это слово остаётся в вашем словаре — его можно менять.</p>
              )}
            </div>
          )}
          {vm && !editing && !vm.authored && (
            <div className="ord-actions-row">
              <button type="button" className="ord-draft-save ghost ord-edit-open" onClick={startEdit}>Редактировать</button>
              <div className="ord-file-col">
                <label className="ord-draft-file">
                  <input type="file" accept=".json,application/json" onChange={handleFile} />
                  Выбрать файл
                </label>
                <span className="ord-file-fmt">формат .json</span>
              </div>
            </div>
          )}
          {vm && !editing && !vm.authored && (
            <div className="ord-paste-row">
              <label className="ord-paste-label">Или вставь текст JSON (ответ ИИ):</label>
              <textarea className="ord-paste-area" rows={4} value={jsonText} onChange={(e) => setJsonText(e.target.value)} placeholder="Вставь сюда JSON целиком…" />
              <button type="button" className="ord-draft-save ghost ord-paste-apply" disabled={!jsonText.trim() || jsonText.trim() === appliedText} onClick={applyJsonText}>Применить</button>
            </div>
          )}
          {vm && vm.authored && (
            <button type="button" className="ord-draft-save ghost ord-view-public" onClick={() => onViewPublic && onViewPublic({ data: makePublicData(), fullPhoto: (vm.photo && vm.photo.dataUrl) || null })}>Смотреть публичную страницу</button>
          )}
          {editing && edit && (
            <div className="ord-edit">
              <label className="ord-f">Слово
                <input value={edit.word} onChange={(e) => setE('word', e.target.value)} />
              </label>
              <div className="ord-f-row">
                <label className="ord-f">Часть речи
                  <select value={edit.pos} onChange={(e) => setE('pos', e.target.value)}>
                    <option value="">—</option>
                    {Object.keys(canonicalCategoryIds).map((name) => (
                      <option key={name} value={name.toLowerCase()}>{name}</option>
                    ))}
                  </select>
                </label>
                <label className="ord-f">Род
                  <select value={edit.gender} onChange={(e) => setE('gender', e.target.value)}>
                    <option value="">—</option>
                    <option value="en">en</option>
                    <option value="ei">ei</option>
                    <option value="et">et</option>
                  </select>
                </label>
              </div>
              <label className="ord-f">Переводы (через запятую)
                <input value={fromList(edit.translations)} onChange={(e) => setEList('translations', e.target.value)} />
              </label>
              <label className="ord-f">Фраза-пример
                <input value={edit.phrase} onChange={(e) => setE('phrase', e.target.value)} />
              </label>
              <label className="ord-f">Перевод фразы
                <input value={edit.phrase_translation} onChange={(e) => setE('phrase_translation', e.target.value)} />
              </label>
              {edit.pos === 'adjektiv' && (
                <div className="ord-f-ex">
                  <span className="ord-f-label">Степени сравнения</span>
                  <div className="ord-f-ex-row">
                    <input placeholder="positiv" value={(edit.gradboyning && edit.gradboyning.positiv) || ''} onChange={(e) => setEGrad('positiv', e.target.value)} />
                    <input placeholder="komparativ" value={(edit.gradboyning && edit.gradboyning.komparativ) || ''} onChange={(e) => setEGrad('komparativ', e.target.value)} />
                    <input placeholder="superlativ" value={(edit.gradboyning && edit.gradboyning.superlativ) || ''} onChange={(e) => setEGrad('superlativ', e.target.value)} />
                  </div>
                </div>
              )}
              <div className="ord-f-ex">
                <span className="ord-f-label">3 примера и переводы</span>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="ord-f-ex-row">
                    <input placeholder={'Пример ' + (i + 1)} value={edit.examples[i] || ''} onChange={(e) => setEArr('examples', i, e.target.value)} />
                    <input placeholder={'Перевод ' + (i + 1)} value={edit.example_translations[i] || ''} onChange={(e) => setEArr('example_translations', i, e.target.value)} />
                  </div>
                ))}
              </div>
              <label className="ord-f">
                <span className="ord-f-head">История <span className="ord-f-count">{(edit.story || '').length} / 1000</span></span>
                <textarea rows={5} maxLength={1000} value={edit.story} onChange={(e) => setE('story', e.target.value)} />
              </label>
              <label className="ord-f">Синонимы (через запятую)
                <input value={fromList(edit.synonyms)} onChange={(e) => setEList('synonyms', e.target.value)} />
              </label>
              <label className="ord-f">Антонимы (через запятую)
                <input value={fromList(edit.antonyms)} onChange={(e) => setEList('antonyms', e.target.value)} />
              </label>
              <label className="ord-f">Корень
                <input value={edit.root} onChange={(e) => setE('root', e.target.value)} />
              </label>
              <label className="ord-f">Однокоренные (через запятую)
                <input value={fromList(edit.cognates)} onChange={(e) => setEList('cognates', e.target.value)} />
              </label>
              <div className="ord-f-ex">
                <span className="ord-f-label">5 похожих по звучанию</span>
                <div className="ord-f-phon">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <input key={i} value={edit.phonetic_similar[i] || ''} onChange={(e) => setEArr('phonetic_similar', i, e.target.value)} />
                  ))}
                </div>
              </div>
              <label className="ord-f">
                <span className="ord-f-head">Этимология <span className="ord-f-count">{(edit.etymology || '').length} / 1000</span></span>
                <textarea rows={3} maxLength={1000} value={edit.etymology} onChange={(e) => setE('etymology', e.target.value)} />
              </label>
              <div className="ord-edit-actions">
                <button type="button" className="ord-draft-save ghost" onClick={() => setEditing(false)}>Отмена</button>
                <button type="button" className="ord-draft-save" onClick={saveEdit}>Сохранить изменения</button>
              </div>
            </div>
          )}
          {result && result.errors.length > 0 && (
            <div className="ord-draft-errors">
              <strong>Не получилось загрузить:</strong>
              <ul>{result.errors.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}
          {result && result.errors.length === 0 && (
            <div className="ord-draft-ok">
              <strong>Данные распознаны ✓</strong>
              {result.warnings.length > 0 && (
                <ul className="ord-draft-warn">{result.warnings.map((m, i) => <li key={i}>{m}</li>)}</ul>
              )}
            </div>
          )}
          {parsed && (
            <div className="ord-draft-summary">
              <p><b>Переводы:</b> {(parsed.translations || []).join(', ')}</p>
              <p><b>Фраза:</b> {parsed.phrase}</p>
              <p><b>Примеров:</b> {(parsed.examples || []).length}, <b>синонимов:</b> {(parsed.synonyms || []).length}, <b>корень:</b> {parsed.root || '—'}</p>
            </div>
          )}
          {parsed && !saved && !hasData && (
            <button type="button" className="ord-draft-save" onClick={() => handleSave('replace')}>
              Сохранить и продолжить →
            </button>
          )}
          {parsed && !saved && hasData && (
            <div className="ord-draft-choice">
              <p>У этого слова уже есть данные. Что сделать?</p>
              <div className="ord-draft-choice-btns">
                <button type="button" className="ord-draft-save ghost" onClick={() => handleSave('fill')}>Дополнить</button>
                <button type="button" className="ord-draft-save" onClick={() => handleSave('replace')}>Заменить</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
